import AddIcon from '@mui/icons-material/Add'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import type { ClientListItem } from '@lawyer/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ErrorAlert } from '../components/common/Feedback'
import { PageHeader } from '../components/common/PageHeader'
import { ClientDialog } from '../components/clients/ClientDialog'
import { api } from '../lib/api'
import { qk } from '../lib/queryClient'

export function ClientsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: qk.clients({ search }),
    queryFn: () => api.get<ClientListItem[]>('/clients', { search: search || undefined }),
  })

  const columns: GridColDef<ClientListItem>[] = [
    {
      field: 'displayName',
      headerName: 'שם',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => navigate(`/clients/${params.row.id}`)}
          sx={{ font: 'inherit', color: 'inherit' }}
        >
          {params.row.displayName}
        </Link>
      ),
    },
    { field: 'name', headerName: 'שם באנגלית', flex: 1, minWidth: 140 },
    { field: 'nationalId', headerName: 'ת״ז', width: 120 },
    { field: 'passport', headerName: 'דרכון', width: 120 },
    { field: 'country', headerName: 'מדינה', width: 110 },
    { field: 'phone', headerName: 'טלפון', width: 130 },
    { field: 'email', headerName: 'דוא״ל', flex: 1, minWidth: 160 },
    { field: 'companyCount', headerName: 'חברות', width: 90, type: 'number' },
    { field: 'caseCount', headerName: 'תיקים', width: 90, type: 'number' },
  ]

  return (
    <>
      <PageHeader
        title="לקוחות"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            לקוח חדש
          </Button>
        }
      />

      <TextField
        label="חיפוש"
        placeholder="שם, ת״ז, דרכון, טלפון או דוא״ל"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ maxWidth: 420, mb: 2 }}
      />

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}

      <Paper variant="outlined" sx={{ height: 'calc(100vh - 300px)', minHeight: 380 }}>
        <DataGrid
          rows={data ?? []}
          columns={columns}
          loading={isFetching}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ border: 0 }}
        />
      </Paper>

      <ClientDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(client) => navigate(`/clients/${client.id}`)}
      />
    </>
  )
}
